/**
 * Signalements (admin) — migration 0604, App Review 1.2 : messages et
 * utilisateurs signalés, à traiter sous 24 heures. « Masquer le message » le
 * retire pour tout le monde (et l'auteur ne peut plus le rétablir) ;
 * « Classer sans suite » ferme le signalement sans rien changer.
 */

import { useState } from 'react';
import { EyeOff, Check, X } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  CONTENT_REPORT_STATUS_LABELS,
  CONTENT_TYPE_LABELS,
  REPORT_REASON_LABELS,
  useContentReports,
  useResolveContentReport,
  type ContentReport,
  type ContentReportFilter,
} from '@/lib/safety/user-safety';

const DATE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function ReportCard({ report }: { report: ContentReport }) {
  const resolve = useResolveContentReport();
  const isMessage = report.contentType !== 'user';
  const open = report.status === 'open';

  return (
    <div className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-700">
          {REPORT_REASON_LABELS[report.reason] ?? report.reason}
        </span>
        <span className="bg-ink-50 text-ink-600 rounded-full px-2 py-0.5 font-semibold">
          {CONTENT_TYPE_LABELS[report.contentType]}
        </span>
        <span className="text-ink-400">{DATE.format(new Date(report.createdAt))}</span>
        {!open && (
          <span className="text-ink-500 font-semibold">
            · {CONTENT_REPORT_STATUS_LABELS[report.status]}
            {report.contentHidden && ' (message masqué)'}
          </span>
        )}
      </div>

      <p className="text-ink-700 text-sm">
        <strong className="text-ink-900">{report.reporterName}</strong> signale{' '}
        {isMessage ? 'un message de ' : ''}
        <strong className="text-ink-900">{report.targetName}</strong>
        {report.targetReportCount > 1 && (
          <span className="text-red-600"> · {report.targetReportCount} signalements au total</span>
        )}
      </p>

      {report.excerpt && (
        <blockquote className="bg-ink-50 text-ink-800 break-words rounded-xl px-3 py-2 text-sm">
          {report.excerpt}
        </blockquote>
      )}
      {report.details && (
        <p className="text-ink-600 break-words text-sm">
          <span className="text-ink-400">Précisions : </span>
          {report.details}
        </p>
      )}

      {open && (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => resolve.mutate({ reportId: report.id, status: 'dismissed' })}
            disabled={resolve.isPending}
            className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border px-4 text-sm font-semibold disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden /> Classer sans suite
          </button>
          {isMessage ? (
            <Button
              onClick={() =>
                resolve.mutate({ reportId: report.id, status: 'actioned', hideContent: true })
              }
              disabled={resolve.isPending}
              className="min-h-[2.75rem] w-full sm:w-auto"
            >
              <EyeOff className="h-4 w-4" aria-hidden /> Masquer le message
            </Button>
          ) : (
            <Button
              onClick={() => resolve.mutate({ reportId: report.id, status: 'actioned' })}
              disabled={resolve.isPending}
              className="min-h-[2.75rem] w-full sm:w-auto"
            >
              <Check className="h-4 w-4" aria-hidden /> Marquer comme traité
            </Button>
          )}
        </div>
      )}
      {resolve.isError && (
        <p role="alert" className="text-sm text-red-600">
          {resolve.error.message}
        </p>
      )}
    </div>
  );
}

export function TutorAdminReportsPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [filter, setFilter] = useState<ContentReportFilter>('open');
  const { data: reports = [], isLoading, isError } = useContentReports(filter, admin);

  if (!admin) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="font-display text-ink-900 text-[24px] font-bold">Signalements</h1>
        <p className="text-ink-500 font-sans text-[14px]">
          Messages et utilisateurs signalés depuis l’app. À traiter sous 24 heures.
        </p>
      </header>

      <div role="tablist" aria-label="Statut" className="mb-4 flex gap-2">
        {(['open', 'closed'] as const).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            onClick={() => setFilter(f)}
            className={cn(
              'min-h-[2.75rem] flex-1 rounded-xl border px-4 text-sm font-semibold sm:flex-none',
              filter === f
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {f === 'open' ? 'À traiter' : 'Traités'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Impossible de charger les signalements.</p>
      ) : reports.length === 0 ? (
        <p className="text-ink-500 text-sm">
          {filter === 'open' ? 'Aucun signalement à traiter.' : 'Aucun signalement traité.'}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {reports.map((r) => (
            <ReportCard key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}
